import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { addDays, format, subDays, subMonths, subWeeks } from 'date-fns';
import { FormApi } from 'informed';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { ButtonCategory, ButtonSize } from 'components/common/Button';
import { DropdownSelectable } from 'components/common/dropdown';
import { DatepickerButton, Form } from 'components/common/form';

type DateRange = 'all' | 'custom' | 'day' | 'week' | 'month';
type DateRangeData = { fromDate: string; toDate: string; range: DateRange };
type UpdateDataParams = { fromDate: string; toDate: string };

type FormValues = { fromDate: number; toDate: number };

type Props = {
  align?: 'left' | 'right';
  category?: ButtonCategory;
  includeAllTimeOption?: boolean;
  initialDateRange?: Exclude<DateRange, 'all' | 'custom'>;
  size?: ButtonSize;
} & (
  | { withUrl: true; updateData?: never }
  | { withUrl?: false; updateData: (params: UpdateDataParams) => void }
);

const DateRangeSelector = ({
  align = 'left',
  category = 'lighter',
  includeAllTimeOption = false,
  initialDateRange,
  size = 'medium',
  updateData,
  withUrl = false
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const router = useRouter();

  const initialRangeValue = useMemo(() => {
    return initialDateRange
      ? initialDateRange
      : includeAllTimeOption
      ? 'all'
      : 'day';
  }, [initialDateRange, includeAllTimeOption]);

  const [state, setState] = useState({
    fromDate: `${format(subDays(dateNow, 30), 'y-MM-dd')}T00:00:00`,
    toDate: `${format(dateNow, 'y-MM-dd')}T23:59:59`,
    range: initialRangeValue
  });

  useEffect(() => {
    if (!withUrl) return;

    const shouldResetFilter = !router.query.fromDate || !router.query.toDate;
    if (shouldResetFilter) {
      formApi.current.setValue('fromDate', null);
      formApi.current.setValue('toDate', null);

      const data: DateRangeData = {
        fromDate: null,
        toDate: null,
        range: initialRangeValue
      };
      setState(data);
    }
  }, [withUrl, router.query]);

  const handleRangeChange = (range: DateRange) => {
    if (range === 'custom') return;

    if (range === 'all') {
      formApi.current.setValue('fromDate', null);
      formApi.current.setValue('toDate', null);

      const data: DateRangeData = { fromDate: null, toDate: null, range };
      setState(data);
      updateRangeData(data);
      return;
    }

    const toDate = new Date();

    const fromDate =
      range === 'month'
        ? subMonths(toDate, 12)
        : range === 'week'
        ? subWeeks(toDate, 12)
        : subDays(toDate, 30);

    formApi.current.setValue('fromDate', fromDate.getTime());
    formApi.current.setValue('toDate', toDate.getTime());

    const data: DateRangeData = {
      fromDate: `${format(fromDate, 'y-MM-dd')}T00:00:00`,
      toDate: `${format(toDate, 'y-MM-dd')}T23:59:59`,
      range
    };
    setState(data);
    updateRangeData(data);
  };

  const handleFromDateChange = (fromDate: number) => {
    const { values } = formApi.current.getState();

    const data: DateRangeData = {
      fromDate: `${format(fromDate, 'y-MM-dd')}T00:00:00`,
      toDate: '',
      range: 'custom'
    };

    if (fromDate > values.toDate) {
      const toDate = addDays(fromDate, 1);
      formApi.current.setValue('toDate', toDate.getTime());
      data.toDate = `${format(toDate, 'y-MM-dd')}T23:59:59`;
    } else {
      data.toDate = state.toDate;
    }

    setState(data);
    updateRangeData(data);
  };

  const handleToDateChange = (toDate: number) => {
    const { values } = formApi.current.getState();

    const data: DateRangeData = {
      fromDate: '',
      toDate: `${format(toDate, 'y-MM-dd')}T23:59:59`,
      range: 'custom'
    };

    if (toDate < values.fromDate) {
      const fromDate = subDays(toDate, 1);
      formApi.current.setValue('fromDate', fromDate.getTime());
      data.fromDate = `${format(fromDate, 'y-MM-dd')}T00:00:00`;
    } else {
      data.fromDate = state.fromDate;
    }

    setState(data);
    updateRangeData(data);
  };

  const updateRangeData = (data: DateRangeData) => {
    if (updateData) updateData(data);
    else if (withUrl) {
      const query = { ...router.query };
      if (query.page) query.page = '1';

      if (data.fromDate && data.toDate) {
        query.fromDate = encodeURIComponent(data.fromDate);
        query.toDate = encodeURIComponent(data.toDate);
      } else {
        delete query.fromDate;
        delete query.toDate;
      }

      router.replace({ query }, undefined, { shallow: true });
    }
  };

  const { range } = state;

  const ranges = {
    ...(includeAllTimeOption ? { all: messages.analyticsRangeAll } : {}),
    day: messages.analyticsRangeDaily,
    week: messages.analyticsRangeWeekly,
    month: messages.analyticsRangeMonthly
  };

  return (
    <div className="range">
      <Form getApi={(form: FormApi<FormValues>) => (formApi.current = form)}>
        <div className="dates">
          <div className="dates-from">
            <DatepickerButton
              align={align}
              ariaLabel={messages.analyticsDateFrom}
              category={category}
              field="fromDate"
              id="analytics-logins-from-date"
              initialValue={
                includeAllTimeOption
                  ? null
                  : Date.parse(subDays(dateNow, 30).toString())
              }
              maxDate={Date.parse(subDays(dateNow, 1).toString())}
              onChange={handleFromDateChange}
              placeholder={messages.analyticsDateFrom}
              size={size}
            />
          </div>

          <div className="divider">{'–'}</div>

          <div className="dates-to">
            <DatepickerButton
              align={align}
              ariaLabel={messages.analyticsDateTo}
              category={category}
              field="toDate"
              id="analytics-logins-to-date"
              initialValue={includeAllTimeOption ? null : dateNow}
              maxDate={dateNow}
              onChange={handleToDateChange}
              placeholder={messages.analyticsDateTo}
              size={size}
            />
          </div>
        </div>
      </Form>

      <div className="select">
        <DropdownSelectable
          align={align}
          category={category}
          defaultValue={range}
          defaultText={messages.commonCustom}
          dropdownWhiteSpace="nowrap"
          onChange={handleRangeChange}
          options={Object.keys(ranges).map((key) => ({
            text: ranges[key],
            value: key
          }))}
          size={size}
          showSelectedText={range !== 'custom'}
          value={range}
        />
      </div>

      <style jsx>{`
        .dates {
          display: flex;
          margin-right: 20px;
        }
        .divider {
          padding: 3px 8px;
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .select {
            margin-top: 10px;
          }
        }
        @media (min-width: ${breakpoints.small}px) {
          .range {
            display: flex;
          }
        }
      `}</style>
    </div>
  );
};

export default DateRangeSelector;
